// src/core/adapters/LondonAdapter.js
import * as londonApi from '@api/london';
import {
  filterStopsByDirection,
  buildNaptanStopIdxMap,
  buildTripMap,
  mergeArrivalsIntoTrips,
  buildStopArrivals,
  buildStopSchedules,
} from './LondonArrivals';

const getUKToday = () => {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(now);
  const map = {};
  parts.forEach(p => { map[p.type] = p.value; });
  return `${map.year}-${map.month}-${map.day}`;
};

export const londonAdapter = {
  getTodayDate: getUKToday,

  disclaimer: 'Not affiliated with or endorsed by Transport for London (TfL). Contains Transport for London data licensed under the Open Government Licence v3.0. Powered by open transit data feeds.',

  // ---- Raw passthroughs ----
  fetchLines: londonApi.fetchLines,
  fetchRouteStops: londonApi.fetchRouteStops,
  fetchLiveBuses: londonApi.fetchLiveBuses,
  fetchArrivals: londonApi.fetchArrivals,
  fetchSchedule: londonApi.fetchSchedule,
  fetchNextTrip: londonApi.fetchNextTrip,
  fetchHeadway: londonApi.fetchHeadway,
  fetchHourMetrics: londonApi.fetchHourMetrics,
  fetchStopMetrics: londonApi.fetchStopMetrics,
  fetchExpectedArrivals: londonApi.fetchExpectedArrivals,

  // ---- Composed methods ----
  fetchRouteOptions: async () => {
    const raw = await londonApi.fetchLines();
    return raw.map(r => ({
      id: String(r.contract_line_no),
      number: r.contract_line_no,
    }));
  },

  fetchArrivalBoard: async (routeId, direction, date) => {
    const stopData = await londonApi.fetchRouteStops(routeId);
    const targetStops = filterStopsByDirection(stopData, direction);

    const sched = await londonApi.fetchSchedule(routeId, date);
    const arr = await londonApi.fetchArrivals(routeId, '');

    const tripMap = buildTripMap(sched, direction);
    const naptanToStopIdx = buildNaptanStopIdxMap(targetStops);
    mergeArrivalsIntoTrips(tripMap, arr, naptanToStopIdx);

    return {
      stops: targetStops,
      trips: Object.values(tripMap),
      rawArrivals: arr,
    };
  },

  fetchStopArrivalDetail: async (routeId, stop, date) => {
    const [arrivalsData, fullScheduleData] = await Promise.all([
      londonApi.fetchArrivals(routeId, stop.naptan_id),
      londonApi.fetchSchedule(routeId, date),
    ]);

    return {
      ...stop,
      stopName: stop.name,
      timingPointCode: stop.timing_point_code,
      direction: stop.direction === 1 ? 'Outbound' : 'Inbound',
      arrivals: buildStopArrivals(arrivalsData),
      schedules: buildStopSchedules(fullScheduleData, stop.id),
    };
  },
};